import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MTBL_LOGO } from "../lib/content";
import LanguageToggle from "./LanguageToggle";

export default function Navbar() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const NAV_LINKS = [
    { key: "home", to: "/" },
    { key: "about", to: "/about" },
    { key: "services", to: "/services" },
    { key: "sectors", to: "/sectors" },
    { key: "opportunities", to: "/opportunities" },
    { key: "impact", to: "/impact" },
    { key: "partnerships", to: "/partnerships" },
    { key: "insights", to: "/insights" },
    { key: "contact", to: "/contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white border-b border-slate-200 shadow-[0_1px_3px_rgba(15,23,42,0.06)]"
          : "bg-white"
      }`}
    >
      <div className="container-mtbl flex items-center justify-between h-24 md:h-28">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center shrink-0 mr-8 md:mr-10 lg:mr-14"
          aria-label="MTBL Management Consultancy — Home"
        >
          <img
            src={MTBL_LOGO}
            alt="MTBL Management Consultancy"
            className="h-14 md:h-16 lg:h-20 w-auto object-contain scale-[1.3] origin-left"
          />
        </Link>

        <nav
          className="hidden lg:flex items-center gap-6 xl:gap-7"
          aria-label="Primary navigation"
        >
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              data-testid={`nav-link-${link.key}`}
              className={({ isActive }) =>
                `text-sm tracking-wide whitespace-nowrap transition-colors ${
                  isActive
                    ? "text-[#0B1A3D] font-semibold"
                    : "text-slate-600 hover:text-[#0B1A3D]"
                }`
              }
            >
              {t(`nav.${link.key}`)}
            </NavLink>
          ))}
          <LanguageToggle />
          <Link
            to="/contact"
            data-testid="nav-cta-engage"
            className="btn-primary ml-1"
          >
            {t("nav.engageCta")}
          </Link>
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <LanguageToggle variant="mobile" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
            className="text-[#0B1A3D] p-2"
            aria-label={t("nav.toggleMenu")}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t border-slate-200"
        >
          <div className="container-mtbl py-6 flex flex-col gap-4">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-nav-link-${link.key}`}
                className={({ isActive }) =>
                  `text-base ${
                    isActive ? "text-[#0B1A3D] font-semibold" : "text-slate-600"
                  }`
                }
              >
                {t(`nav.${link.key}`)}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-cta-engage"
              className="btn-primary mt-2 w-max"
            >
              {t("nav.engageCta")}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
